<template>
  <div :class="[$style.formBox, getFileListStyle]">
    <div v-if="noUploadedFiles && !fileFormatError"
         :class="$style.emptyList">
      <p>You haven’t added any files yet.</p>
    </div>
    <div v-if="noUploadedFiles && fileFormatError">
      <p :class="$style.error">{{ errorMessage }}</p>
    </div>
    <div v-if="!noUploadedFiles"
         :class="$style.fileList">
      <div v-for="(file, index) in getProofFiles"
           :key="index"
           :class="$style.fileItem">
        <div :class="$style.fileName">
          <img src="@/assets/icons/file.png" alt="File Icon" />
          <span @click="download(file.id, file.fileName)">
            {{ file.fileName }}
          </span>
        </div>

        <span :class="$style.removeBtn"
              @click="removeFile(file.id)">remove</span>
      </div>
      <p v-if="fileFormatError"
         :class="$style.error">{{ errorMessage }}</p>
    </div>
    <div :class="$style.uploadForm">
      <app-input-file v-model="uploadedFiles"
                      accept-files=".jpg, .jpeg, .png, .pdf, .csv, .doc, .docx, .xls, .xlsx, .zip"
                      ref="file"
                      @input="fileChange" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { forceFileDownload } from '@/utils';

export default {
  props: {
    filesType: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      uploadedFiles: [],
      fileFormatError: false,
      downloadError: '',
      errorMessage: '',
    };
  },
  methods: {
    ...mapActions([
      'fetchProofFiles',
      'postProofFile',
      'deleteProofFile',
      'downloadProofFile',
    ]),
    fileChange(e) {
      this.fileFormatError = false;
      const newFiles = e;

      const formData = new FormData();
      for (let i = 0; i < newFiles.length; i += 1) {
        const file = newFiles[i];
        formData.append('files', file, file.name);
      }

      this.postProofFile({
        formData,
        userId: this.getUserId,
        type: this.filesType,
      }).then((resp) => {
        const { filesWithWarnings } = resp.data;

        if (filesWithWarnings.length) {
          const filesList = filesWithWarnings.join(', ');

          this.fileFormatError = true;
          this.errorMessage = `There was an error at uploading the next files: ${filesList}.`;
        } else {
          this.fileFormatError = false;
          this.errorMessage = '';
        }
      }).catch((err) => {
        this.fileFormatError = true;
        this.errorMessage = err.response.data.title;
      });
    },
    removeFile(id) {
      this.fileFormatError = false;
      this.errorMessage = '';
      this.deleteProofFile({
        id,
        userId: this.getUserId,
      }).then(() => {
        this.uploadedFiles = [];
      });
    },
    download(fileId, fileName) {
      this.downloadProofFile({
        id: fileId,
        userId: this.getUserId,
      }).then((resp) => {
        this.downloadError = '';
        forceFileDownload(resp, fileName);
      }).catch((err) => {
        if (err.response.status === 404) {
          this.downloadError = 'File not found';
        } else if (err.response.status.toString().startsWith('5')) {
          this.downloadError = 'Internal server error';
        }
      });
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
      'getProofFiles',
      'getFilesWithWarnings',
    ]),
    noUploadedFiles() {
      return this.getProofFiles.length === 0;
    },
    getFileListStyle() {
      if (this.filesType) {
        return this.$style.shortFileList;
      }

      return this.$style.longFileList;
    },
  },
  mounted() {
    this.fetchProofFiles({
      userId: this.getUserId,
      type: this.filesType,
    }).then(() => {
      this.uploadedFiles = this.getProofFiles;
    });
  },
};
</script>

<style lang="scss" module>
@import "@/styles/variables.scss";
$border-color: #f2f2f2;
$empty-list-color: #a0a0a0;
$file-item-border-color: #c9c9c9;

.formBox {
  width: 100%;
  background-color: $white-color;
  border-radius: 4px;
  box-shadow: $forms-shadow;
  text-align: center;

  .emptyList {
    display: flex;
    justify-content: center;
    align-items: center;

    p {
      color: $empty-list-color;
      font-size: 1.3rem;
      font-style: italic;
      margin: 0;
    }
  }

  .fileList {
    padding: 0 20px;
  }

  &.longFileList {
    .emptyList {
      height: 320px;
    }

    .fileList {
      padding-bottom: 160px;
    }
  }

  &.shortFileList {
    .emptyList {
      height: 60px;
    }
  }

  .uploadForm {
    border-top: 1px solid $border-color;
    padding: 26px;

    label {
      justify-content: center;
    }
  }
}

.fileItem {
  border-bottom: 1px solid $file-item-border-color;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;

  .fileName {
    display: flex;
    align-items: center;

    img {
      max-width: 22px;
      margin-right: 16px;
    }

    span {
      font-family: $link-font;
      font-size: 1.7rem;
      color: $left-sidebar-color;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .removeBtn {
    font-family: $link-font;
    font-size: 1.7rem;
    color: $removeBtnColor;
    text-decoration: underline;
    cursor: pointer;
  }
}

.error {
  font-family: $main-font;
  color: red;
  text-align: left;
  font-size: 1.4rem;
  font-style: italic;
  margin: 20px;
}
</style>
